import './AccountInfo.less';

import { ToastContainer } from '@bfe/moly';
import { useLayoutEffect, useMemo } from 'react';

import SettingGroup from '@/components/SettingGroup';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useI18nContext } from '@/i18n/I18n';
import { useDeadPoolStore } from '@/store/useDeadPool';
import { useUserStore } from '@/store/useUser';
import { complianceHide, complianceShow } from '@/utils/complianceHelpers';

import AccountActivity from './components/accountActivity';
import AffiliateEuForward from './components/affiliateEuForward';
import AvatarSetting from './components/Avatar';
import KycAuth from './components/kycAuth/KycAuth';
import NickNameSecurityLevel from './components/nickNameSecurity';
import TaxJurisdiction from './components/TaxJurisdiction';
import ThirdAccountBind from './components/ThirdAccountBind';
import TradingViewAlertEntry from './components/TradingViewAlertEntry';
import TrustedDevice from './components/TrustedDevice';

const componentGroups = [
  // 账户基本信息
  {
    title: '',
    key: 'base-info',
    components: [
      {
        key: 'AvatarSetting',
        element: AvatarSetting,
        identityHiddenConfig: ['submember'],
      },
      {
        key: 'AffiliateEuForward',
        element: AffiliateEuForward,
        identityHiddenConfig: ['submember'],
      },
      {
        key: 'KycAuth',
        element: KycAuth,
        identityHiddenConfig: ['submember'],
      },
      {
        key: 'TaxJurisdiction',
        element: TaxJurisdiction,
        complianceHiddenConfig: (config: any) => {
          return !complianceShow(config?.TaxJurisdiction);
        },
      },
    ],
  },
  {
    title: 'accountExpansion',
    key: 'account-expansion',
    components: [
      {
        key: 'ThirdAccountBind',
        element: ThirdAccountBind,
        identityHiddenConfig: ['submember'],
        complianceHiddenConfig: (config: any) => {
          return complianceHide(config?.Telegram);
        },
        deadPoolHiddenConfig: (config: any, deadPoolLoading: boolean) => {
          const showThirdBindFromLocalStorage =
            localStorage.getItem('showTelegramBind') === '1';
          if (showThirdBindFromLocalStorage) {
            return false;
          }
          if (deadPoolLoading) {
            return true;
          }
          try {
            const deadPoolShow = JSON.parse(
              config?.['cht.thirdBindGray.config']?.value,
            )?.telegram;
            return !deadPoolShow;
          } catch (error) {
            return true;
          }
        },
      },
      {
        key: 'TradingViewAlert',
        element: TradingViewAlertEntry,
        identityHiddenConfig: ['islamicAccount'],
        complianceHiddenConfig: (config: any) => {
          return complianceHide(config?.TradingViewAlert);
        },
      },
    ],
  },
  {
    title: 'Account Activities',
    key: 'account-activity',
    components: [
      {
        key: 'TrustedDeviceMgmt',
        element: TrustedDevice,
      },
      {
        key: 'AccountActivity',
        element: AccountActivity,
      },
    ],
  },
];

const AccountInfo = ({ loadFn }: { loadFn: () => void }) => {
  const [$t] = useI18nContext();
  const { accountIdentity } = useUserProfile();
  const { complianceConfig } = useUserStore();
  const { deadPoolConfig, loading: deadPoolLoading } = useDeadPoolStore();

  useLayoutEffect(() => {
    loadFn();
  }, []);

  const visibleComponentGroups = useMemo(() => {
    return componentGroups
      .map(({ components, ...item }) => {
        const visibleComponents = components.filter((component: any) => {
          const {
            identityHiddenConfig,
            complianceHiddenConfig,
            deadPoolHiddenConfig,
          } = component;
          let hiddenIdentity = false; // 根据用户身份隐藏
          let hiddenCompliance = false; // 根据合规隐藏
          let hiddenDeadPool = false; // 根据deadpool隐藏
          if (identityHiddenConfig) {
            hiddenIdentity = identityHiddenConfig.some((identity: string) =>
              accountIdentity.includes(identity),
            );
          }
          if (typeof complianceHiddenConfig === 'function') {
            hiddenCompliance = complianceHiddenConfig(complianceConfig);
          }
          if (typeof deadPoolHiddenConfig === 'function') {
            hiddenDeadPool = deadPoolHiddenConfig(
              deadPoolConfig,
              deadPoolLoading,
            );
          }
          return !hiddenIdentity && !hiddenCompliance && !hiddenDeadPool;
        });
        return { ...item, components: visibleComponents };
      })
      .filter(({ components }) => {
        // 过滤掉没有设置项的模块
        return components.length > 0;
      });
  }, [accountIdentity, deadPoolConfig, complianceConfig, deadPoolLoading]);

  return (
    <>
      <div className="account-info-security-info">
        <NickNameSecurityLevel />
      </div>
      {visibleComponentGroups.map(({ title, components, key }) => {
        return (
          <SettingGroup key={key} title={title ? $t(title) : ''}>
            {components.map(({ key, element: Element }) => (
              <Element key={key} />
            ))}
          </SettingGroup>
        );
      })}
      <ToastContainer />
    </>
  );
};

export default AccountInfo;
